import React, { useState } from 'react';
import { ArrowLeft, Users, Calendar, Zap, CreditCard, ChevronDown, Pen, Trash, Plus, Minus, Check, UserPlus, Repeat, MapPin, ShoppingBag, X } from 'lucide-react';
import { Product } from '../../types';
interface CheckoutScreenProps {
  items: Product[];
  onBack: () => void;
  onPlaceOrder: () => void;
}
interface OrderItem extends Product {
  quantity: number;
  assignedTo: string[];
}
interface Participant {
  id: string;
  name: string;
}
interface AdvancedPickup {
  date: string;
  timeWindow: string;
  isRecurring: boolean;
  frequency: 'daily' | 'weekly';
  pickupNotes: string;
}
export function CheckoutScreen({
  items,
  onBack,
  onPlaceOrder
}: CheckoutScreenProps) {
  // Convert cart items to order items with assignment tracking
  const [orderItems, setOrderItems] = useState<OrderItem[]>(items.map(item => ({
    ...item,
    quantity: (item as any).quantity || 1,
    assignedTo: []
  })));
  // Group Order State
  const [isGroupOrder, setIsGroupOrder] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([{
    id: '1',
    name: 'You'
  }]);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipantName, setNewParticipantName] = useState('');
  // Advanced Pickup State
  const [isAdvancedPickup, setIsAdvancedPickup] = useState(false);
  const [advancedPickup, setAdvancedPickup] = useState<AdvancedPickup>({
    date: new Date().toISOString().split('T')[0],
    timeWindow: '12:30 PM',
    isRecurring: false,
    frequency: 'weekly',
    pickupNotes: ''
  });
  // Regular Pickup Time
  const [selectedTime, setSelectedTime] = useState('12:30 PM');
  const recentPickupTimes = ['12:30 PM', '1:00 PM', '1:30 PM', 'In 15 mins', 'In 30 mins'];
  const timeWindows = ['12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM'];
  // Other State
  const [isExpressPickup, setIsExpressPickup] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [showSpecialInstructions, setShowSpecialInstructions] = useState(false);
  const [lastSpecialInstructions, setLastSpecialInstructions] = useState('');
  const defaultPayment = {
    type: 'Visa',
    last4: '4242',
    expiry: '04/24'
  };
  // Calculate totals
  const subtotal = orderItems.reduce((sum, item) => {
    if (isGroupOrder && item.assignedTo.length > 0) {
      return sum + item.price * item.assignedTo.length;
    }
    return sum + item.price * item.quantity;
  }, 0);
  const tax = subtotal * 0.08;
  const total = subtotal + tax;
  // Group Order Functions
  const addParticipant = () => {
    if (newParticipantName.trim()) {
      setParticipants([...participants, {
        id: Date.now().toString(),
        name: newParticipantName.trim()
      }]);
      setNewParticipantName('');
      setShowAddParticipant(false);
    }
  };
  const removeParticipant = (id: string) => {
    setParticipants(participants.filter(p => p.id !== id));
    // Remove assignments for this participant
    setOrderItems(orderItems.map(item => ({
      ...item,
      assignedTo: item.assignedTo.filter(pId => pId !== id)
    })));
  };
  const assignItemToParticipant = (itemName: string, participantId: string) => {
    setOrderItems(orderItems.map(item => {
      if (item.name === itemName) {
        const isAssigned = item.assignedTo.includes(participantId);
        return {
          ...item,
          assignedTo: isAssigned ? item.assignedTo.filter(id => id !== participantId) : [...item.assignedTo, participantId]
        };
      }
      return item;
    }));
  };
  const calculateParticipantTotal = (participantId: string) => {
    return orderItems.reduce((sum, item) => {
      if (item.assignedTo.includes(participantId)) {
        return sum + item.price;
      }
      return sum;
    }, 0);
  };
  // Order Item Functions
  const removeItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };
  const clearCart = () => {
    setOrderItems([]);
  };
  const handlePlaceOrder = () => {
    // Validate group order assignments
    if (isGroupOrder) {
      const unassignedItems = orderItems.filter(item => item.assignedTo.length === 0);
      if (unassignedItems.length > 0) {
        alert('Please assign all items to participants before placing order');
        return;
      }
    }
    onPlaceOrder();
  };
  return <div className="pb-24 min-h-screen bg-gray-50" data-id="element-104">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-5 py-6 flex items-center gap-3 sticky top-0 z-10" data-id="element-105">
        <button onClick={onBack} className="w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-106">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-107" />
        </button>
        <h1 className="text-2xl font-bold text-white" data-id="element-108">Checkout</h1>
      </div>

      <div className="flex-1 px-5 py-4" data-id="element-109">
        {/* Group Order Toggle */}
        <div className="mb-6" data-id="element-110">
          <button onClick={() => setIsGroupOrder(!isGroupOrder)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all
              ${isGroupOrder ? 'border-[#FFFC00] bg-[#FFFBE6]' : 'border-gray-100 bg-white'}`} data-id="element-111">
            <div className="flex items-center" data-id="element-112">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3
                ${isGroupOrder ? 'bg-[#FFFC00]' : 'bg-gray-100'}`} data-id="element-113">
                <Users size={20} className="text-black" data-id="element-114" />
              </div>
              <div className="text-left" data-id="element-115">
                <h3 className="font-medium" data-id="element-116">Group Order</h3>
                <p className="text-sm text-gray-500" data-id="element-117">
                  Split items & coordinate pickup
                </p>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
              ${isGroupOrder ? 'border-[#FFFC00] bg-[#FFFC00]' : 'border-gray-300'}`} data-id="element-118">
              {isGroupOrder && <Check size={14} className="text-black" data-id="element-119" />}
            </div>
          </button>
        </div>

        {/* Group Order Section */}
        {isGroupOrder && <div className="mb-6" data-id="element-120">
            <div className="flex items-center justify-between mb-3" data-id="element-121">
              <h2 className="text-lg font-bold" data-id="element-122">Participants</h2>
              <button onClick={() => setShowAddParticipant(true)} className="bg-[#FFFC00] text-black p-2 rounded-full hover:bg-[#F7C300] transition-all" data-id="element-123">
                <UserPlus size={20} data-id="element-124" />
              </button>
            </div>

            {/* Participants List */}
            <div className="space-y-2 mb-4" data-id="element-125">
              {participants.map(participant => {
            const total = calculateParticipantTotal(participant.id);
            const hasItems = orderItems.some(item => item.assignedTo.includes(participant.id));
            return <div key={participant.id} className={`flex items-center justify-between p-3 rounded-xl transition-all ${hasItems ? 'bg-[#F0EEFF]' : 'bg-gray-100'}`} data-id="element-126">
                    <div className="flex items-center" data-id="element-127">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${hasItems ? 'bg-[#FFFBE6]' : 'bg-gray-200'}`} data-id="element-128">
                        <span className="text-sm font-medium" data-id="element-129">
                          {participant.name[0].toUpperCase()}
                        </span>
                      </div>
                      <div data-id="element-130">
                        <p className="font-medium" data-id="element-131">{participant.name}</p>
                        {hasItems ? <p className="text-sm text-[#A020F0]" data-id="element-132">
                            ${total.toFixed(2)}
                          </p> : <p className="text-sm text-gray-500" data-id="element-133">No items yet</p>}
                      </div>
                    </div>
                    {participant.id !== '1' && <button onClick={() => removeParticipant(participant.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-134">
                        <Trash size={18} data-id="element-135" />
                      </button>}
                  </div>;
          })}
            </div>

            {/* Add Participant Form */}
            {showAddParticipant && <div className="bg-[#F0EEFF] p-3 rounded-xl animate-in fade-in duration-200" data-id="element-136">
                <div className="flex items-center space-x-2" data-id="element-137">
                  <input type="text" placeholder="Participant name" value={newParticipantName} onChange={e => setNewParticipantName(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-[#FFFC00] outline-none" data-id="element-138" />
                  <button onClick={addParticipant} className="bg-[#FFFC00] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#F7C300] transition-all" data-id="element-139">
                    Add
                  </button>
                </div>
              </div>}
          </div>}

        {/* Advanced Pickup Toggle */}
        <div className="mb-6" data-id="element-140">
          <button onClick={() => setIsAdvancedPickup(!isAdvancedPickup)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all
              ${isAdvancedPickup ? 'border-[#FFFC00] bg-[#FFFBE6]' : 'border-gray-100 bg-white'}`} data-id="element-141">
            <div className="flex items-center" data-id="element-142">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3
                ${isAdvancedPickup ? 'bg-[#FFFC00]' : 'bg-gray-100'}`} data-id="element-143">
                <Calendar size={20} className="text-black" data-id="element-144" />
              </div>
              <div className="text-left" data-id="element-145">
                <h3 className="font-medium" data-id="element-146">Advanced Pickup</h3>
                <p className="text-sm text-gray-500" data-id="element-147">
                  Schedule future & recurring orders
                </p>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
              ${isAdvancedPickup ? 'border-[#FFFC00] bg-[#FFFC00]' : 'border-gray-300'}`} data-id="element-148">
              {isAdvancedPickup && <Check size={14} className="text-black" data-id="element-149" />}
            </div>
          </button>
        </div>

        {/* Advanced Pickup Options */}
        {isAdvancedPickup && <div className="mb-6 space-y-4" data-id="element-150">
            {/* Date Selection */}
            <div data-id="element-151">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-152">
                Pickup Date
              </label>
              <input type="date" min={new Date().toISOString().split('T')[0]} value={advancedPickup.date} onChange={e => setAdvancedPickup({
            ...advancedPickup,
            date: e.target.value
          })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-[#FFFC00] outline-none" data-id="element-153" />
            </div>

            {/* Time Window Selection */}
            <div data-id="element-154">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-155">
                Pickup Window
              </label>
              <div className="flex overflow-x-auto scrollbar-hide -mx-1 pb-2" data-id="element-156">
                {timeWindows.map(time => <button key={time} onClick={() => setAdvancedPickup({
              ...advancedPickup,
              timeWindow: time
            })} className={`flex-shrink-0 mx-1 px-4 py-2 rounded-xl text-sm font-medium transition-all
                      ${advancedPickup.timeWindow === time ? 'bg-[#A020F0] text-white' : 'bg-[#F0EEFF] text-gray-700 hover:bg-[#A020F0]/10'}`} data-id="element-157">
                    {time}
                  </button>)}
              </div>
            </div>

            {/* Recurring Order Toggle */}
            <div className="bg-[#F0EEFF] rounded-xl p-4" data-id="element-158">
              <div className="flex items-center justify-between" data-id="element-159">
                <div className="flex items-center" data-id="element-160">
                  <Repeat className="w-5 h-5 mr-3 text-[#A020F0]" data-id="element-161" />
                  <div data-id="element-162">
                    <h3 className="font-medium" data-id="element-163">Recurring Order</h3>
                    <p className="text-sm text-gray-500" data-id="element-164">
                      Automatically place this order
                    </p>
                  </div>
                </div>
                <button onClick={() => setAdvancedPickup({
              ...advancedPickup,
              isRecurring: !advancedPickup.isRecurring
            })} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                    ${advancedPickup.isRecurring ? 'border-[#FFFC00] bg-[#FFFC00]' : 'border-gray-300'}`} data-id="element-165">
                  {advancedPickup.isRecurring && <Check size={14} className="text-black" data-id="element-166" />}
                </button>
              </div>

              {advancedPickup.isRecurring && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-167">
                  <div className="flex gap-2" data-id="element-168">
                    <button onClick={() => setAdvancedPickup({
                ...advancedPickup,
                frequency: 'daily'
              })} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all
                        ${advancedPickup.frequency === 'daily' ? 'bg-[#FFFC00] text-black' : 'bg-gray-100 text-gray-600'}`} data-id="element-169">
                      Daily
                    </button>
                    <button onClick={() => setAdvancedPickup({
                ...advancedPickup,
                frequency: 'weekly'
              })} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all
                        ${advancedPickup.frequency === 'weekly' ? 'bg-[#FFFC00] text-black' : 'bg-gray-100 text-gray-600'}`} data-id="element-170">
                      Weekly
                    </button>
                  </div>
                </div>}
            </div>

            {/* Pickup Location Notes */}
            <div data-id="element-171">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-172">
                Pickup Notes
              </label>
              <div className="relative" data-id="element-173">
                <MapPin size={20} className="absolute left-3 top-3 text-gray-400" data-id="element-174" />
                <input type="text" placeholder="E.g., Park in spot #2" value={advancedPickup.pickupNotes || ''} onChange={e => setAdvancedPickup({
              ...advancedPickup,
              pickupNotes: e.target.value
            })} className="w-full pl-10 pr-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-[#FFFC00] outline-none" data-id="element-175" />
              </div>
            </div>
          </div>}

        {/* Regular Pickup Time Selection */}
        {!isAdvancedPickup && <div className="mb-6" data-id="element-176">
            <h2 className="text-lg font-bold mb-3" data-id="element-177">Pickup Time</h2>
            <div className="flex overflow-x-auto scrollbar-hide -mx-1 pb-2" data-id="element-178">
              {recentPickupTimes.map(time => <button key={time} onClick={() => setSelectedTime(time)} className={`flex-shrink-0 mx-1 px-4 py-2 rounded-xl text-sm font-medium transition-all
                    ${selectedTime === time ? 'bg-[#A020F0] text-white' : 'bg-[#F0EEFF] text-gray-700 hover:bg-[#A020F0]/10'}`} data-id="element-179">
                  {time}
                </button>)}
            </div>
          </div>}

        {/* Order Items Section */}
        <div className="mb-6" data-id="element-180">
          <div className="flex items-center justify-between mb-3" data-id="element-181">
            <h2 className="text-lg font-bold" data-id="element-182">Order Items</h2>
            {orderItems.length > 0 && <button onClick={clearCart} className="text-gray-500 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-gray-100" title="Clear Cart" data-id="element-183">
                <Trash size={18} data-id="element-184" />
              </button>}
          </div>

          {orderItems.length === 0 ? <div className="bg-[#F0EEFF] rounded-xl p-6 text-center" data-id="element-185">
              <ShoppingBag size={24} className="mx-auto mb-2 text-gray-400" data-id="element-186" />
              <p className="text-gray-500" data-id="element-187">Your cart is empty</p>
              <button onClick={() => onBack()} className="mt-4 text-sm text-[#A020F0] font-medium hover:underline" data-id="element-188">
                Return to Menu
              </button>
            </div> : <div className="space-y-3" data-id="element-189">
              {orderItems.map((item, index) => <div key={index} className="bg-[#F0EEFF] rounded-xl p-4" data-id="element-190">
                  <div className="flex justify-between items-start mb-2" data-id="element-191">
                    <div className="flex-1" data-id="element-192">
                      <div className="flex items-start justify-between" data-id="element-193">
                        <div data-id="element-194">
                          <h3 className="font-medium" data-id="element-195">{item.name}</h3>
                          <p className="text-[#A020F0] font-medium mt-1" data-id="element-196">
                            ${item.price.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-197">
                          <div className="flex items-center bg-white rounded-lg shadow-sm" data-id="element-198">
                            <button onClick={() => {
                        if (item.quantity > 1) {
                          setOrderItems(prev => prev.map((i, idx) => idx === index ? {
                            ...i,
                            quantity: i.quantity - 1
                          } : i));
                        }
                      }} className="p-1.5 hover:bg-gray-50 rounded-l-lg transition-colors" disabled={item.quantity <= 1} data-id="element-199">
                              <Minus size={14} className={item.quantity <= 1 ? 'text-gray-300' : 'text-gray-600'} data-id="element-200" />
                            </button>
                            <span className="w-8 text-center text-sm font-medium" data-id="element-201">
                              {item.quantity}
                            </span>
                            <button onClick={() => {
                        setOrderItems(prev => prev.map((i, idx) => idx === index ? {
                          ...i,
                          quantity: i.quantity + 1
                        } : i));
                      }} className="p-1.5 hover:bg-gray-50 rounded-r-lg transition-colors" data-id="element-202">
                              <Plus size={14} className="text-gray-600" data-id="element-203" />
                            </button>
                          </div>
                          <p className="font-medium min-w-[60px] text-right" data-id="element-204">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                          <button onClick={() => removeItem(index)} className="text-gray-400 hover:text-red-500 transition-colors p-1.5 hover:bg-white rounded-lg" title="Remove Item" data-id="element-205">
                            <Trash size={16} data-id="element-206" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Group Order Assignment Section */}
                  {isGroupOrder && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-207">
                      <div className="flex justify-between items-center mb-2" data-id="element-208">
                        <p className="text-sm text-gray-500" data-id="element-209">Assign to:</p>
                        {item.assignedTo.length === 0 && <p className="text-xs text-amber-600" data-id="element-210">
                            Not assigned yet
                          </p>}
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-211">
                        {participants.map(participant => {
                  const isAssigned = item.assignedTo.includes(participant.id);
                  return <button key={participant.id} onClick={() => assignItemToParticipant(item.name, participant.id)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all
                                ${isAssigned ? 'bg-[#FFFC00] text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} 
                                flex items-center space-x-1`} data-id="element-212">
                              <span data-id="element-213">{participant.name}</span>
                              {isAssigned && <Check size={14} className="ml-1" data-id="element-214" />}
                            </button>;
                })}
                      </div>
                      {item.assignedTo.length > 0 && <div className="mt-2 space-y-1" data-id="element-215">
                          <div className="text-xs text-gray-500" data-id="element-216">
                            {item.assignedTo.length}{' '}
                            {item.assignedTo.length === 1 ? 'person' : 'people'}{' '}
                            selected
                          </div>
                          <div className="text-xs font-medium text-[#A020F0]" data-id="element-217">
                            ${item.price.toFixed(2)} × {item.assignedTo.length}{' '}
                            = $
                            {(item.price * item.assignedTo.length).toFixed(2)}
                          </div>
                        </div>}
                    </div>}
                </div>)}
            </div>}
        </div>

        {/* Payment Method */}
        <div className="bg-[#F0EEFF] rounded-xl p-4 mb-6" data-id="element-218">
          <div className="flex items-center justify-between" data-id="element-219">
            <div className="flex items-center" data-id="element-220">
              <CreditCard className="w-5 h-5 mr-3 text-[#A020F0]" data-id="element-221" />
              <div data-id="element-222">
                <h3 className="font-medium" data-id="element-223">
                  {defaultPayment.type} ****{defaultPayment.last4}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-224">
                  Expires {defaultPayment.expiry}
                </p>
              </div>
            </div>
            <button className="text-[#A020F0] text-sm font-medium" data-id="element-225">
              Change
            </button>
          </div>
        </div>

        {/* Express Pickup Toggle */}
        <div className="mb-6" data-id="element-226">
          <button onClick={() => setIsExpressPickup(!isExpressPickup)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all
              ${isExpressPickup ? 'border-[#FFFC00] bg-[#FFFBE6]' : 'border-gray-100 bg-white'}`} data-id="element-227">
            <div className="flex items-center" data-id="element-228">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3
                ${isExpressPickup ? 'bg-[#FFFC00]' : 'bg-gray-100'}`} data-id="element-229">
                <Zap size={20} className="text-black" data-id="element-230" />
              </div>
              <div className="text-left" data-id="element-231">
                <h3 className="font-medium" data-id="element-232">Express Pickup</h3>
                <p className="text-sm text-gray-500" data-id="element-233">
                  Prioritize order (+$1.99)
                </p>
              </div>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
              ${isExpressPickup ? 'border-[#FFFC00] bg-[#FFFC00]' : 'border-gray-300'}`} data-id="element-234">
              {isExpressPickup && <Check size={14} className="text-black" data-id="element-235" />}
            </div>
          </button>
        </div>

        {/* Order Summary */}
        <div className="mb-6" data-id="element-236">
          <button onClick={() => setShowOrderSummary(!showOrderSummary)} className="w-full flex items-center justify-between mb-2" data-id="element-237">
            <h2 className="text-lg font-bold" data-id="element-238">Order Summary</h2>
            <ChevronDown size={20} className={`text-gray-400 transition-transform ${showOrderSummary ? 'rotate-180' : ''}`} data-id="element-239" />
          </button>
          {showOrderSummary && <div className="space-y-3 animate-in fade-in duration-200" data-id="element-240">
              {orderItems.map((item, index) => <div key={index} className="flex justify-between text-sm" data-id="element-241">
                  <span data-id="element-242">
                    {item.quantity}x {item.name}
                  </span>
                  <span data-id="element-243">${(item.price * item.quantity).toFixed(2)}</span>
                </div>)}
              <div className="border-t border-gray-100 pt-3 space-y-2" data-id="element-244">
                <div className="flex justify-between text-sm" data-id="element-245">
                  <span data-id="element-246">Subtotal</span>
                  <span data-id="element-247">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-248">
                  <span data-id="element-249">Tax</span>
                  <span data-id="element-250">${tax.toFixed(2)}</span>
                </div>
                {isExpressPickup && <div className="flex justify-between text-sm" data-id="element-251">
                    <span data-id="element-252">Express Pickup</span>
                    <span data-id="element-253">$1.99</span>
                  </div>}
              </div>
            </div>}
        </div>

        {/* Special Instructions */}
        <div className="mb-8" data-id="element-254">
          <button onClick={() => setShowSpecialInstructions(!showSpecialInstructions)} className="w-full flex items-center justify-between mb-2" data-id="element-255">
            <div className="flex items-center" data-id="element-256">
              <h2 className="text-lg font-bold" data-id="element-257">Special Instructions</h2>
              {lastSpecialInstructions && !showSpecialInstructions && <span className="ml-2 text-sm text-gray-500" data-id="element-258">
                  ({lastSpecialInstructions})
                </span>}
            </div>
            <Pen size={18} className="text-gray-400" data-id="element-259" />
          </button>
          {showSpecialInstructions && <div className="animate-in fade-in duration-200" data-id="element-260">
              <textarea defaultValue={lastSpecialInstructions} onChange={e => setLastSpecialInstructions(e.target.value)} placeholder="Add special instructions..." className="w-full p-3 rounded-xl border-2 border-gray-100 focus:border-[#FFFC00] outline-none h-24 text-sm" data-id="element-261" />
            </div>}
        </div>

        {/* Checkout Button */}
        <button onClick={handlePlaceOrder} disabled={orderItems.length === 0} className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200 ${orderItems.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#FFFC00] text-black hover:bg-[#F7C300]'}`} data-id="element-262">
          {isAdvancedPickup ? `Schedule ${advancedPickup.isRecurring ? 'Recurring ' : ''}Order` : isGroupOrder ? 'Confirm Group Order' : 'Place Order'}{' '}
          (${(total + (isExpressPickup ? 1.99 : 0)).toFixed(2)})
        </button>
      </div>
    </div>;
}